import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Logo, Wordmark } from "@/components/Logo";
import { MapPin, Phone, User, ClipboardText, LockKey, Warning, CheckCircle, CurrencyDollar, FilePdf, Receipt, Plus, X, Camera, ImageSquare } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

const PRIORITY = {
  urgent: "bg-red-500 text-white",
  high: "bg-orange-500 text-white",
  normal: "bg-slate-800 text-white",
  low: "bg-slate-300 text-slate-700",
};

export default function SubPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [adhocOpen, setAdhocOpen] = useState(false);

  const load = () => {
    axios.get(`${BASE}/api/public/sub/${token}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.status === 404 ? "This link is no longer active. Ask the office to send you a new one." : "Something went wrong."));
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (err) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="rugged-card bg-white p-8 max-w-md text-center" data-testid="sub-portal-error">
        <Warning weight="fill" size={40} className="mx-auto text-amber-500"/>
        <h1 className="font-display font-black text-2xl mt-3">Link Not Active</h1>
        <p className="mt-2 text-sm text-slate-600 font-medium">{err}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500 font-bold uppercase tracking-widest">Loading…</div>;

  const quoteCount = data.quote_requests?.length || 0;
  const readyCount = data.ready_to_invoice?.length || 0;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="sub-portal">
      <header className="bg-[#0f172a] text-white">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-white flex items-center justify-center"><Logo size={28}/></div>
            <div>
              <Wordmark dark size="text-xl"/>
              <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">Sub-Contractor Portal</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-black uppercase tracking-widest text-slate-400">Signed in as</div>
            <div className="font-bold">{data.sub.name}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-8">
        <div className="flex justify-end">
          <button onClick={() => setAdhocOpen(true)} data-testid="new-adhoc-btn"
            className="tactile-btn h-11 px-4 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2">
            <Plus weight="bold" size={16}/> New Invoice (no job)
          </button>
        </div>
        {/* QUOTE REQUESTS */}
        {quoteCount > 0 && (
          <section data-testid="quote-section">
            <div className="flex items-baseline justify-between">
              <h1 className="font-display font-black text-xl sm:text-2xl tracking-tight flex items-center gap-2">
                <CurrencyDollar weight="fill" size={22} className="text-emerald-600"/> Quote Requests
              </h1>
              <div className="text-xs font-black uppercase tracking-widest text-emerald-700">{quoteCount} pending</div>
            </div>
            <p className="mt-1 text-xs text-slate-500 font-medium">Office needs your price on these. Once accepted they become normal jobs.</p>
            <div className="mt-3 space-y-4">
              {data.quote_requests.map(j => <QuoteCard key={j.id} j={j} token={token} onSaved={load}/>)}
            </div>
          </section>
        )}

        {/* OPEN JOBS */}
        <section>
          <div className="flex items-baseline justify-between">
            <h1 className="font-display font-black text-xl sm:text-2xl tracking-tight">Your Open Jobs</h1>
            <div className="text-xs font-black uppercase tracking-widest text-slate-500" data-testid="sub-portal-count">{data.jobs.length} open</div>
          </div>
          <div className="mt-3 space-y-4">
            {data.jobs.map(j => <OpenJobCard key={j.id} j={j} token={token} onSaved={load}/>)}
            {data.jobs.length === 0 && (
              <div className="rugged-card bg-white p-10 text-center">
                <CheckCircle weight="fill" size={40} className="mx-auto text-emerald-500"/>
                <p className="mt-3 font-bold text-slate-500">All caught up.</p>
                <p className="text-sm text-slate-400">No open jobs assigned to you right now.</p>
              </div>
            )}
          </div>
        </section>

        {/* READY TO INVOICE */}
        {readyCount > 0 && (
          <section data-testid="invoice-section">
            <div className="flex items-baseline justify-between">
              <h1 className="font-display font-black text-xl sm:text-2xl tracking-tight flex items-center gap-2">
                <Receipt weight="fill" size={22} className="text-[#00C4CC]"/> Ready to Invoice
              </h1>
              <div className="text-xs font-black uppercase tracking-widest text-[#0e7490]">{readyCount} completed</div>
            </div>
            <p className="mt-1 text-xs text-slate-500 font-medium">Type your invoice amount below. Office gets a bell notification instantly.</p>
            <div className="mt-3 space-y-4">
              {data.ready_to_invoice.map(j => <InvoiceCard key={j.id} j={j} token={token} onSaved={load}/>)}
            </div>
          </section>
        )}

        {/* SUBMITTED (history) */}
        {(data.submitted_invoices || []).length > 0 && (
          <section>
            <h2 className="font-display font-black text-lg tracking-tight text-slate-500">Recent Invoices</h2>
            <div className="mt-3 space-y-2">
              {data.submitted_invoices.map(j => (
                <div key={j.id} className="bg-white rounded-lg px-4 py-3 flex items-center justify-between text-sm border-l-4 border-slate-300" data-testid={`inv-hist-${j.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate">{j.title}</div>
                    <div className="text-xs text-slate-500 truncate">{j.property_address} · Submitted {new Date(j.sub_invoice_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right ml-2">
                    <div className="font-black font-mono">${(j.sub_invoice_amount||0).toFixed(2)}</div>
                    {j.sub_invoice_paid_at ? (
                      <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600">PAID</div>
                    ) : (
                      <div className="text-[10px] font-black uppercase tracking-widest text-amber-600">Pending</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="text-center text-[10px] font-black uppercase tracking-widest text-slate-400 pt-4">
          Bookmark this page — refreshes every 30s
        </div>
      </main>
      {adhocOpen && <AdhocModal token={token} onClose={() => setAdhocOpen(false)} onSaved={() => { setAdhocOpen(false); load(); }}/>}
    </div>
  );
}

function AdhocModal({ token, onClose, onSaved }) {
  const [f, setF] = useState({ property_address: "", description: "", amount: "", invoice_number: "", is_pass_through: false });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = Number(f.amount);
    if (!f.description.trim()) { toast.error("Describe the work"); return; }
    if (!(n > 0)) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/api/public/sub/${token}/adhoc-invoice`, {
        property_address: f.property_address, description: f.description,
        amount: n, invoice_number: f.invoice_number,
        is_pass_through: !!f.is_pass_through,
      });
      if (file && data.job_id) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${BASE}/api/public/sub/${token}/invoice-pdf/${data.job_id}`, fd);
      }
      toast.success("Invoice sent — office notified");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not send")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-2xl border-t-4 sm:border-4 border-[#0f172a] overflow-hidden" data-testid="adhoc-modal">
        <div className="flex items-center justify-between p-4 border-b-2 border-slate-100">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Ad-hoc invoice</div>
            <div className="font-display font-black text-xl">New Invoice — No Job</div>
          </div>
          <button onClick={onClose} data-testid="adhoc-close" className="p-1"><X weight="bold" size={22}/></button>
        </div>
        <div className="p-4 space-y-3">
          <input value={f.property_address} onChange={e => setF({...f, property_address: e.target.value})}
            data-testid="adhoc-address" placeholder="Property address (or 'No address')"
            className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
          <textarea value={f.description} onChange={e => setF({...f, description: e.target.value})}
            data-testid="adhoc-desc" rows={2} placeholder="What did you do? (short description)"
            className="w-full px-3 py-2 border-2 border-[#0f172a] text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
              <input type="number" step="0.01" value={f.amount} onChange={e => setF({...f, amount: e.target.value})}
                data-testid="adhoc-amount" placeholder="0.00"
                className="w-full h-11 pl-7 pr-3 border-2 border-[#00C4CC] font-mono font-black"/>
            </div>
            <input value={f.invoice_number} onChange={e => setF({...f, invoice_number: e.target.value})}
              data-testid="adhoc-num" placeholder="Invoice # (optional)"
              className="h-11 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={f.is_pass_through}
              onChange={e => setF({...f, is_pass_through: e.target.checked})}
              data-testid="adhoc-passthrough" className="h-4 w-4 accent-[#0f172a]"/>
            <span className="font-medium">Pass-through (another vendor's work)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-600">
            <FilePdf weight="bold" size={16} className="text-slate-400"/>
            <span>Attach PDF/photo (optional)</span>
            <input type="file" accept="application/pdf,image/*"
              onChange={e => setFile(e.target.files[0] || null)}
              data-testid="adhoc-pdf" className="text-xs"/>
          </label>
          <button onClick={submit} disabled={busy} data-testid="adhoc-submit"
            className="tactile-btn w-full h-12 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-sm tracking-widest disabled:opacity-50">
            {busy ? "Sending…" : "Send Invoice to Office"}
          </button>
        </div>
      </div>
    </div>
  );
}

function JobHeader({ j }) {
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`h-6 px-2 font-black uppercase text-[10px] tracking-widest inline-flex items-center ${PRIORITY[j.priority] || PRIORITY.normal}`}>{j.priority}</span>
        {j.work_order_number && <span className="h-6 px-2 bg-[#0f172a] text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center font-mono">WO# {j.work_order_number}</span>}
      </div>
      <h2 className="mt-2 font-display font-black text-lg leading-tight">{j.title}</h2>
      {j.description && <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{j.description}</p>}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        {j.property_address && (
          <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(j.property_address)}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 text-blue-700 font-bold underline underline-offset-2">
            <MapPin weight="fill" size={16}/> {j.property_address}
          </a>
        )}
        {j.customer_name && <div className="flex items-center gap-2 text-slate-700"><User weight="bold" size={16} className="text-slate-400"/>{j.customer_name}</div>}
        {j.customer_phone && <a href={`tel:${j.customer_phone}`} className="flex items-center gap-2 text-slate-700 font-bold"><Phone weight="bold" size={16} className="text-slate-400"/>{j.customer_phone}</a>}
        {j.scheduled_for && <div className="flex items-center gap-2 text-slate-700"><ClipboardText weight="bold" size={16} className="text-slate-400"/>Scheduled {j.scheduled_for}</div>}
      </div>
    </>
  );
}

function OpenJobCard({ j, token, onSaved }) {
  const [uploading, setUploading] = useState("");
  const [closing, setClosing] = useState(false);
  const [notes, setNotes] = useState("");
  const beforeCount = (j.before_photos || []).length;
  const afterCount = (j.after_photos || []).length;

  const uploadPhoto = async (slot, files) => {
    if (!files || !files.length) return;
    setUploading(slot);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${BASE}/api/public/sub/${token}/jobs/${j.id}/photo?slot=${slot}`, fd);
      }
      toast.success(`Uploaded ${files.length} ${slot} photo${files.length === 1 ? "" : "s"}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Photo upload failed"));
    } finally { setUploading(""); }
  };

  const complete = async () => {
    if (afterCount < 1) { toast.error("Add at least one 'after' photo first"); return; }
    setClosing(true);
    try {
      await axios.post(`${BASE}/api/public/sub/${token}/jobs/${j.id}/complete`, { completion_notes: notes });
      toast.success("Job closed — office notified");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not close job"));
    } finally { setClosing(false); }
  };

  return (
    <div data-testid={`sub-job-${j.id}`} className="rugged-card bg-white p-5">
      <JobHeader j={j}/>
      {(j.entry_code || j.lockbox_code || j.entry_notes) && (
        <div className="mt-3 bg-amber-50 border-l-4 border-amber-500 px-3 py-2 text-xs text-slate-700">
          <div className="flex items-center gap-1 font-black uppercase tracking-widest text-amber-700 mb-0.5"><LockKey weight="fill" size={12}/> Access</div>
          {j.entry_code && <div>Entry code: <span className="font-mono font-black">{j.entry_code}</span></div>}
          {j.lockbox_code && <div>Lockbox: <span className="font-mono font-black">{j.lockbox_code}</span></div>}
          {j.entry_notes && <div className="mt-1">{j.entry_notes}</div>}
        </div>
      )}

      {/* Job Photos section */}
      <div className="mt-4 border-2 border-slate-200 rounded-lg p-3" data-testid={`sub-photos-${j.id}`}>
        <div className="flex items-center gap-2 mb-2">
          <ImageSquare weight="fill" size={16} className="text-[#00C4CC]"/>
          <span className="text-[11px] font-black uppercase tracking-widest text-slate-700">Job Photos</span>
          <span className="ml-auto text-[10px] text-slate-500 font-bold">
            {beforeCount} before · {afterCount} after
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className={`h-11 border-2 border-dashed border-slate-400 rounded font-black uppercase text-[11px] tracking-widest inline-flex items-center justify-center gap-1 cursor-pointer hover:bg-slate-50 ${uploading==="before" ? "opacity-50" : ""}`}>
            <Camera weight="bold" size={14}/> {uploading==="before" ? "Uploading…" : "+ Before"}
            <input type="file" accept="image/*" capture="environment" multiple hidden
              data-testid={`sub-upload-before-${j.id}`}
              onChange={(e) => { uploadPhoto("before", e.target.files); e.target.value = ""; }}/>
          </label>
          <label className={`h-11 border-2 border-dashed border-emerald-500 rounded text-emerald-700 font-black uppercase text-[11px] tracking-widest inline-flex items-center justify-center gap-1 cursor-pointer hover:bg-emerald-50 ${uploading==="after" ? "opacity-50" : ""}`}>
            <Camera weight="bold" size={14}/> {uploading==="after" ? "Uploading…" : "+ After"}
            <input type="file" accept="image/*" capture="environment" multiple hidden
              data-testid={`sub-upload-after-${j.id}`}
              onChange={(e) => { uploadPhoto("after", e.target.files); e.target.value = ""; }}/>
          </label>
        </div>
      </div>

      {/* Complete section */}
      <div className="mt-4 border-2 border-[#0f172a] rounded-lg p-3">
        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Completion notes (optional)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
          data-testid={`sub-complete-notes-${j.id}`} rows={2}
          placeholder="What did you do? Any follow-up needed?"
          className="mt-1 w-full px-3 py-2 border-2 border-slate-300 rounded text-sm resize-none"/>
        <button onClick={complete} disabled={closing || afterCount < 1}
          data-testid={`sub-complete-btn-${j.id}`}
          className="mt-2 w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center justify-center gap-2 disabled:opacity-50">
          <CheckCircle weight="fill" size={16}/>
          {closing ? "Closing…" : afterCount < 1 ? "Add 1 After photo to close" : "Mark Complete"}
        </button>
      </div>
    </div>
  );
}

function QuoteCard({ j, token, onSaved }) {
  const [price, setPrice] = useState(j.sub_quoted_price || "");
  const [notes, setNotes] = useState(j.sub_quote_notes || "");
  const [busy, setBusy] = useState(false);
  const [showAllPhotos, setShowAllPhotos] = useState(false);

  // Gather photos from move-out inspection rooms + top-level before_photos
  const roomPhotos = [];
  const mo = j.move_out_inspection || {};
  Object.entries(mo).forEach(([room, entry]) => {
    (entry?.photos || []).forEach(p => {
      roomPhotos.push({ url: p.url || p, room });
    });
  });
  const beforePhotos = (j.before_photos || []).map(p => ({ url: p.url || p, room: "" }));
  const allPhotos = [...roomPhotos, ...beforePhotos];
  const visiblePhotos = showAllPhotos ? allPhotos : allPhotos.slice(0, 6);

  const specs = j.quote_property_specs || {};
  const rc = j.room_config || {};
  const sizeBits = [];
  if (rc.bedrooms) sizeBits.push(`${rc.bedrooms} bed`);
  if (rc.bathrooms) sizeBits.push(`${rc.bathrooms} bath`);
  if (specs.sq_ft) sizeBits.push(`${Number(specs.sq_ft).toLocaleString()} sq ft`);
  if (specs.stories) sizeBits.push(`${specs.stories} stor${specs.stories === 1 ? "y" : "ies"}`);
  if (specs.year_built) sizeBits.push(`built ${specs.year_built}`);
  const sizeLine = sizeBits.join(" · ");

  const submit = async () => {
    const n = Number(price);
    if (!(n > 0)) { toast.error("Enter a valid price"); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/public/sub/${token}/quote/${j.id}`, { price: n, notes });
      toast.success("Quote sent — office notified");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not send")); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`quote-card-${j.id}`} className="rugged-card bg-white p-5 border-l-4 border-emerald-500">
      <JobHeader j={j}/>

      {sizeLine && (
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`quote-size-${j.id}`}>
          <span className="h-5 px-1.5 bg-fuchsia-600 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center">
            Property Size
          </span>
          <span className="text-xs font-bold text-slate-700">{sizeLine}</span>
        </div>
      )}

      {j.quote_summary && (
        <div className="mt-3 p-3 border-2 border-fuchsia-500 bg-fuchsia-50" data-testid={`quote-summary-${j.id}`}>
          <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 mb-1">Scope of Work</div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{j.quote_summary}</div>
        </div>
      )}

      {allPhotos.length > 0 && (
        <div className="mt-3" data-testid={`quote-photos-${j.id}`}>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
            Inspection Photos ({allPhotos.length})
          </div>
          <div className="grid grid-cols-3 gap-2">
            {visiblePhotos.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noreferrer"
                data-testid={`quote-photo-${j.id}-${i}`}
                className="block relative aspect-square bg-slate-100 border-2 border-slate-300 overflow-hidden hover:border-fuchsia-500">
                <img src={p.url} alt={p.room || "inspection"} className="w-full h-full object-cover"/>
                {p.room && (
                  <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[9px] font-black uppercase tracking-widest px-1 py-0.5 truncate">
                    {p.room}
                  </span>
                )}
              </a>
            ))}
          </div>
          {allPhotos.length > 6 && (
            <button onClick={() => setShowAllPhotos(!showAllPhotos)}
              data-testid={`quote-photos-toggle-${j.id}`}
              className="mt-2 text-[10px] font-black uppercase tracking-widest text-fuchsia-700 hover:underline">
              {showAllPhotos ? "Show fewer" : `Show all ${allPhotos.length} →`}
            </button>
          )}
        </div>
      )}

      {j.description && !j.quote_summary && (
        <div className="mt-3 p-3 border-l-2 border-slate-300 bg-slate-50 text-sm text-slate-700 whitespace-pre-wrap" data-testid={`quote-desc-${j.id}`}>
          {j.description}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-slate-200">
        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-2">Your Quote</div>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
            <input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)}
              data-testid={`quote-price-${j.id}`}
              placeholder="0.00"
              className="w-full h-12 pl-7 pr-3 border-2 border-emerald-500 font-mono text-lg font-black"/>
          </div>
          <button onClick={submit} disabled={busy} data-testid={`quote-submit-${j.id}`}
            className="tactile-btn h-12 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-50">
            {busy ? "Sending…" : "Send Quote"}
          </button>
        </div>
        <input value={notes} onChange={e => setNotes(e.target.value)}
          data-testid={`quote-notes-${j.id}`} placeholder="Notes for the office (optional)"
          className="mt-2 w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
      </div>
    </div>
  );
}

function InvoiceCard({ j, token, onSaved }) {
  const [amount, setAmount] = useState(j.sub_invoice_amount || j.sub_quoted_price || "");
  const [invNum, setInvNum] = useState(j.sub_invoice_number || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = Number(amount);
    if (!(n > 0)) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/public/sub/${token}/invoice/${j.id}`, { amount: n, invoice_number: invNum });
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${BASE}/api/public/sub/${token}/invoice-pdf/${j.id}`, fd);
      }
      toast.success("Invoice sent — office notified");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not send")); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`invoice-card-${j.id}`} className="rugged-card bg-white p-5 border-l-4 border-[#00C4CC]">
      <JobHeader j={j}/>
      {j.work_performed_polished && (
        <div className="mt-2 p-2 bg-slate-50 text-xs text-slate-600 border-l-2 border-slate-300">
          <div className="font-black uppercase text-[9px] tracking-widest text-slate-400">Crew notes</div>
          {j.work_performed_polished}
        </div>
      )}
      {(j.materials || []).length > 0 && (
        <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
          Materials logged: ${(j.materials || []).reduce((s, m) => s + (m.quantity || 0) * (m.unit_cost || 0), 0).toFixed(2)}
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-slate-200">
        <div className="text-[10px] font-black uppercase tracking-widest text-[#0e7490] mb-2">Your Invoice</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
            <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
              data-testid={`inv-amount-${j.id}`} placeholder="0.00"
              className="w-full h-12 pl-7 pr-3 border-2 border-[#00C4CC] font-mono text-lg font-black"/>
          </div>
          <input value={invNum} onChange={e => setInvNum(e.target.value)}
            data-testid={`inv-num-${j.id}`} placeholder="Invoice #"
            className="h-12 px-3 border-2 border-slate-300 text-sm"/>
        </div>
        <label className="mt-2 flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-600">
          <FilePdf weight="bold" size={16} className="text-slate-400"/>
          <span>Attach PDF/photo (optional)</span>
          <input type="file" accept="application/pdf,image/*"
            onChange={e => setFile(e.target.files[0] || null)}
            data-testid={`inv-pdf-${j.id}`}
            className="text-xs"/>
        </label>
        <button onClick={submit} disabled={busy} data-testid={`inv-submit-${j.id}`}
          className="tactile-btn mt-3 w-full h-12 bg-[#00C4CC] hover:bg-[#00a8af] text-white font-black uppercase text-xs tracking-widest disabled:opacity-50">
          {busy ? "Sending…" : "Send Invoice to Office"}
        </button>
      </div>
    </div>
  );
}
